import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FileText, 
  Download, 
  Plus, 
  Calendar, 
  Filter,
  Search,
  Eye,
  Edit,
  Trash2,
  CheckCircle,
  AlertTriangle,
  XCircle
} from 'lucide-react';

const ComplianceReporting = () => {
  const [selectedReport, setSelectedReport] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const reportTemplates = [
    { id: 1, name: 'ISO 27001 Compliance Report', type: 'ISO', status: 'active', lastUsed: '2 days ago', description: 'Information security management system compliance report' },
    { id: 2, name: 'PCI DSS Quarterly Review', type: 'PCI', status: 'active', lastUsed: '1 week ago', description: 'Payment card industry data security standards review' },
    { id: 3, name: 'FedRAMP Security Assessment', type: 'FedRAMP', status: 'active', lastUsed: '2 weeks ago', description: 'Federal risk and authorization management program assessment' },
    { id: 4, name: 'CJIS Compliance Check', type: 'CJIS', status: 'active', lastUsed: '1 month ago', description: 'Criminal justice information services compliance verification' },
    { id: 5, name: 'ATF Compliance Summary', type: 'ATF', status: 'draft', lastUsed: 'Never', description: 'Bureau of Alcohol, Tobacco, Firearms compliance summary' },
    { id: 6, name: 'SOC 2 Type II Report', type: 'SOC2', status: 'active', lastUsed: '3 months ago', description: 'Service organization control type 2 compliance report' }
  ];

  const generatedReports = [
    { id: 1, name: 'ISO 27001 Q2 2024', template: 'ISO 27001 Compliance Report', generated: '2 days ago', status: 'completed', size: '2.4 MB', format: 'PDF' },
    { id: 2, name: 'PCI DSS Q1 2024', template: 'PCI DSS Quarterly Review', generated: '1 week ago', status: 'completed', size: '1.8 MB', format: 'PDF' },
    { id: 3, name: 'FedRAMP Annual 2024', template: 'FedRAMP Security Assessment', generated: '2 weeks ago', status: 'completed', size: '3.2 MB', format: 'PDF' },
    { id: 4, name: 'CJIS Monthly Check', template: 'CJIS Compliance Check', generated: '1 month ago', status: 'completed', size: '0.9 MB', format: 'PDF' },
    { id: 5, name: 'ATF Compliance Draft', template: 'ATF Compliance Summary', generated: 'Never', status: 'draft', size: '0 KB', format: 'Draft' }
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'active':
        return <CheckCircle className="w-5 h-5 text-blue-400" />;
      case 'draft':
        return <AlertTriangle className="w-5 h-5 text-yellow-400" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-400" />;
      default:
        return <FileText className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-400';
      case 'active':
        return 'text-blue-400';
      case 'draft':
        return 'text-yellow-400';
      case 'error':
        return 'text-red-400';
      default:
        return 'text-gray-400';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'active':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'draft':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'error':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const filteredReports = generatedReports.filter(report => {
    const matchesSearch = report.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         report.template.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || report.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">Compliance Reporting</h1>
        <p className="text-gray-400 mt-1">Generate and manage compliance reports and documentation</p>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card hover:bg-gray-700/50 transition-colors cursor-pointer"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <Plus className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Create New Report</h3>
              <p className="text-gray-400">Generate custom compliance report</p>
            </div>
          </div>
        </motion.button>

        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card hover:bg-gray-700/50 transition-colors cursor-pointer"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
              <Download className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Export All</h3>
              <p className="text-gray-400">Download all recent reports</p>
            </div>
          </div>
        </motion.button>

        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card hover:bg-gray-700/50 transition-colors cursor-pointer"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <Calendar className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Schedule Reports</h3>
              <p className="text-gray-400">Set up automated reporting</p>
            </div>
          </div>
        </motion.button>
      </div>

      {/* Report Templates */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="card"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white">Report Templates</h2>
          <button className="btn-primary flex items-center">
            <Plus className="w-4 h-4 mr-2" />
            New Template
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {reportTemplates.map((template) => (
            <motion.div
              key={template.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="p-4 bg-gray-700 rounded-lg border border-gray-600 hover:border-gray-500 transition-colors"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="font-medium text-white mb-1">{template.name}</h3>
                  <p className="text-sm text-gray-400">{template.description}</p>
                </div>
                {getStatusIcon(template.status)}
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">Type: {template.type}</span>
                <span className="text-gray-400">Last: {template.lastUsed}</span>
              </div>
              
              <div className="flex items-center space-x-2 mt-3">
                <button className="p-2 text-blue-400 hover:bg-blue-500/20 rounded transition-colors">
                  <Eye className="w-4 h-4" />
                </button>
                <button className="p-2 text-green-400 hover:bg-green-500/20 rounded transition-colors">
                  <Edit className="w-4 h-4" />
                </button>
                <button className="p-2 text-red-400 hover:bg-red-500/20 rounded transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Generated Reports */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="card"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white">Generated Reports</h2>
          <div className="flex items-center space-x-4">
            {/* Search */}
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search reports..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
              />
            </div>
            
            {/* Filter */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
            >
              <option value="all">All Status</option>
              <option value="completed">Completed</option>
              <option value="draft">Draft</option>
              <option value="error">Error</option>
            </select>
          </div>
        </div>
        
        <div className="space-y-3">
          {filteredReports.map((report) => (
            <motion.div
              key={report.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center justify-between p-4 bg-gray-700 rounded-lg border border-gray-600 hover:border-gray-500 transition-colors"
            >
              <div className="flex items-center space-x-4">
                <FileText className="w-8 h-8 text-blue-400" />
                <div>
                  <h3 className="font-medium text-white">{report.name}</h3>
                  <p className="text-sm text-gray-400">{report.template}</p>
                  <div className="flex items-center space-x-4 text-xs text-gray-500 mt-1">
                    <span>Generated: {report.generated}</span>
                    <span>Size: {report.size}</span>
                    <span>Format: {report.format}</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusBadgeColor(report.status)}`}>
                  {report.status}
                </span>
                
                <div className="flex items-center space-x-2">
                  <button className="p-2 text-blue-400 hover:bg-blue-500/20 rounded transition-colors" title="View">
                    <Eye className="w-4 h-4" />
                  </button>
                  <button className="p-2 text-green-400 hover:bg-green-500/20 rounded transition-colors" title="Download">
                    <Download className="w-4 h-4" />
                  </button>
                  <button className="p-2 text-red-400 hover:bg-red-500/20 rounded transition-colors" title="Delete">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Report Statistics */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="grid grid-cols-1 md:grid-cols-4 gap-6"
      >
        <div className="card text-center">
          <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center mx-auto mb-3">
            <FileText className="w-6 h-6 text-blue-400" />
          </div>
          <h3 className="text-2xl font-bold text-white">{generatedReports.length}</h3>
          <p className="text-gray-400">Total Reports</p>
        </div>
        
        <div className="card text-center">
          <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center mx-auto mb-3">
            <CheckCircle className="w-6 h-6 text-green-400" />
          </div>
          <h3 className="text-2xl font-bold text-white">{generatedReports.filter(r => r.status === 'completed').length}</h3>
          <p className="text-gray-400">Completed</p>
        </div>
        
        <div className="card text-center">
          <div className="w-12 h-12 bg-yellow-500/20 rounded-lg flex items-center justify-center mx-auto mb-3">
            <AlertTriangle className="w-6 h-6 text-yellow-400" />
          </div>
          <h3 className="text-2xl font-bold text-white">{generatedReports.filter(r => r.status === 'draft').length}</h3>
          <p className="text-gray-400">Drafts</p>
        </div>
        
        <div className="card text-center">
          <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center mx-auto mb-3">
            <Calendar className="w-6 h-6 text-purple-400" />
          </div>
          <h3 className="text-2xl font-bold text-white">6</h3>
          <p className="text-gray-400">Templates</p>
        </div>
      </motion.div>
    </div>
  );
};

export default ComplianceReporting;
